import { useEffect, useMemo, useRef, useState } from 'react';
import { posVendaApi } from '@/features/pos-venda/api/posVendaApi';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { VARIAVEIS_ENVIO_OS } from '@/features/pos-venda/variaveisMensagem';
import { SUGESTOES_ENVIO_OS } from '@/features/pos-venda/textosSugeridos';
import { SugestoesTexto } from '@/features/pos-venda/components/SugestoesTexto';

// =====================================================================
// Modal "Enviar no WhatsApp" da tela da ordem de serviço — o atendente
// dispara, na hora, o resumo do que foi feito para o cliente. O texto é
// renderizado de novo (autoritativo) no servidor; aqui mostramos uma prévia
// com os dados desta OS para o atendente conferir antes de enviar.
//
// O texto (template) que o atendente personaliza fica salvo no localStorage
// e é reusado em toda OS — ele monta a mensagem do jeito dele uma vez só.
// =====================================================================

export type DadosOSWhatsapp = {
  id: string;
  numero: string;
  clienteNome: string;
  clienteTelefone: string | null;
  placa: string | null;
  /** Placa quando existe; senão "Marca Modelo Ano". Usado no token {{placa}}. */
  veiculoDescricao: string;
  data: string;
  total: number;
  colaboradorResponsavelNome: string | null;
  itens: Array<{ tipo: string; descricao: string | null; quantidade: number }>;
};

const fmtBRL = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtData = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
const fmtQtd = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 3 });

// Variáveis oferecidas como "chips" para o atendente clicar e inserir.
const VARIAVEIS = VARIAVEIS_ENVIO_OS;

function rotuloItem(it: { descricao: string | null; quantidade: number }): string {
  const nome = (it.descricao ?? '').trim() || 'Item';
  return it.quantidade && it.quantidade !== 1 ? `${nome} (${fmtQtd.format(it.quantidade)}x)` : nome;
}

const TEMPLATE_PADRAO = SUGESTOES_ENVIO_OS[0].texto;

// O template personalizado do atendente fica salvo localmente e vale para
// todas as OS (ele escreve do jeito dele uma vez).
const STORAGE_KEY = 'oa:balcao-whatsapp-template';

function lerTemplateSalvo(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? TEMPLATE_PADRAO;
  } catch {
    return TEMPLATE_PADRAO;
  }
}

export function EnviarOrcamentoWhatsappModal({
  dados,
  aberto,
  onFechar,
}: {
  dados: DadosOSWhatsapp;
  aberto: boolean;
  onFechar: () => void;
}) {
  const [texto, setTexto] = useState(lerTemplateSalvo);
  const [enviando, setEnviando] = useState(false);
  const [feedback, setFeedback] = useState<{ tipo: 'erro' | 'sucesso'; msg: string } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Salva a personalização localmente sempre que o texto muda.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, texto);
    } catch {
      /* armazenamento indisponível — segue sem persistir */
    }
  }, [texto]);

  function inserirVariavel(chave: string) {
    const token = `{{${chave}}}`;
    const ta = textareaRef.current;
    if (ta && typeof ta.selectionStart === 'number') {
      const ini = ta.selectionStart;
      const fim = ta.selectionEnd;
      const novo = texto.slice(0, ini) + token + texto.slice(fim);
      setTexto(novo);
      requestAnimationFrame(() => {
        ta.focus();
        ta.selectionStart = ta.selectionEnd = ini + token.length;
      });
    } else {
      setTexto(texto + token);
    }
  }

  // Contexto local (aproximado) para a prévia. {{km}}, {{veiculo}} e
  // {{acompanhamento}} são preenchidos de fato pelo servidor no envio.
  const contexto = useMemo<Record<string, string>>(() => {
    const servicos = dados.itens.filter((i) => i.tipo === 'Servico').map(rotuloItem);
    const pecas = dados.itens.filter((i) => i.tipo === 'Produto').map(rotuloItem);
    return {
      cliente: dados.clienteNome || '',
      // Sem placa, o token cai para "Marca Modelo Ano" — a mensagem continua
      // identificando o carro em vez de sair com um buraco.
      placa: dados.placa ?? dados.veiculoDescricao,
      numero: dados.numero,
      total: fmtBRL.format(dados.total || 0),
      servicos: servicos.join(', '),
      pecas: pecas.join(', '),
      itens: dados.itens.map((i) => `• ${rotuloItem(i)}`).join('\n'),
      data: dados.data ? fmtData.format(new Date(dados.data)) : '',
      responsavel: dados.colaboradorResponsavelNome ?? '',
      acompanhamento: '[link de acompanhamento]',
    };
  }, [dados]);

  const previa = useMemo(
    () =>
      texto.replace(/\{\{\s*([\wÀ-ſ]+)\s*\}\}/g, (_t, k: string) => {
        const chave = k.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
        return Object.prototype.hasOwnProperty.call(contexto, chave) ? contexto[chave] : '';
      }),
    [texto, contexto],
  );

  async function enviar() {
    if (!texto.trim()) {
      setFeedback({ tipo: 'erro', msg: 'Escreva a mensagem antes de enviar.' });
      return;
    }
    setEnviando(true);
    setFeedback(null);
    try {
      await posVendaApi.enviarOS(dados.id, texto);
      setFeedback({ tipo: 'sucesso', msg: `Mensagem enviada para ${dados.clienteNome}.` });
    } catch (e) {
      setFeedback({ tipo: 'erro', msg: e instanceof Error ? e.message : 'Falha ao enviar.' });
    } finally {
      setEnviando(false);
    }
  }

  if (!aberto) return null;

  const semTelefone = !dados.clienteTelefone;

  return (
    <Modal
      aberto={aberto}
      titulo="Enviar no WhatsApp"
      onFechar={onFechar}
      rodape={
        <>
          <Button onClick={onFechar}>Fechar</Button>
          <Button variante="primary" onClick={() => void enviar()} disabled={enviando || semTelefone}>
            {enviando ? 'Enviando…' : 'Enviar agora'}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <p className="hint" style={{ marginTop: 0 }}>
          Para <strong>{dados.clienteNome}</strong>
          {dados.clienteTelefone ? ` · ${dados.clienteTelefone}` : ''}. Monte o texto do seu jeito —
          ele fica salvo e vale para as próximas OS. O pós-venda preenche os dados reais ao enviar.
        </p>

        {semTelefone && (
          <p className="alert alert--error" role="alert">
            Este cliente não tem telefone cadastrado. Cadastre o telefone para enviar pelo WhatsApp.
          </p>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {VARIAVEIS.map((v) => (
            <button
              key={v.chave}
              type="button"
              className="btn btn--ghost"
              style={{ fontSize: 12, padding: '2px 8px' }}
              onClick={() => inserirVariavel(v.chave)}
              title={`Inserir ${v.rotulo}`}
            >
              + {v.rotulo}
            </button>
          ))}
        </div>

        <textarea
          ref={textareaRef}
          className="textarea"
          rows={4}
          style={{ width: '100%' }}
          value={texto}
          maxLength={4000}
          placeholder={TEMPLATE_PADRAO}
          onChange={(e) => setTexto(e.target.value)}
        />

        <SugestoesTexto sugestoes={SUGESTOES_ENVIO_OS} texto={texto} onTexto={setTexto} />

        <div
          style={{
            background: 'var(--color-surface-muted)',
            border: '1px solid var(--color-border)',
            borderRadius: 8,
            padding: 12,
          }}
        >
          <span
            style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--color-text-subtle)' }}
          >
            Prévia
          </span>
          <p style={{ margin: 0, whiteSpace: 'pre-wrap', color: 'var(--color-text)' }}>{previa}</p>
        </div>

        {feedback &&
          (feedback.tipo === 'erro' ? (
            <p role="alert" className="alert alert--error">
              {feedback.msg}
            </p>
          ) : (
            <p role="status" style={{ margin: 0, color: 'var(--color-success)', fontWeight: 600 }}>
              ✓ {feedback.msg}
            </p>
          ))}
      </div>
    </Modal>
  );
}
